import { DatePicker as AntDatePicker } from 'antd';
import type { DatePickerProps } from 'antd/es/date-picker';
import type { Dayjs } from 'dayjs';
import { useId, useMemo, useState } from 'react';
import cn from 'classnames';

import styles from './date-picker.module.scss';
import { CalendarIcon } from '../icons/calendar';
import { ChevronLeftIcon } from '../icons/chevron-left';
import { ChevronRightIcon } from '../icons/chevron-right';

export type AppDatePickerProps = DatePickerProps<Dayjs> & {
    label?: string;
    requiredMark?: boolean;
    footnoteMark?: boolean;
    placeholder?: string;
};

export const AppDatePicker = ({
    label,
    value,
    defaultValue,
    onFocus,
    onBlur,
    onOpenChange,
    popupClassName,
    requiredMark,
    footnoteMark,
    className,
    suffixIcon,
    disabled,
    format,
    placeholder,
    ...rest
}: AppDatePickerProps) => {
    const id = useId();
    const [focused, setFocused] = useState(false);
    const [open, setOpen] = useState(false);

    const hasValue = useMemo(() => {
        const v = value ?? defaultValue;
        if (Array.isArray(v)) {
            return v.filter(Boolean).length > 0;
        }

        return v !== undefined && v !== null && String(v ?? '').length > 0;
    }, [value, defaultValue]);

    const shouldFloatLabel = focused || open || hasValue;

    return (
        <div
            className={cn(styles.root, className, {
                [styles.isFocused]: focused || open,
                [styles.isDisabled]: disabled,
                [styles.hasValue]: hasValue,
            })}
        >
            <AntDatePicker<Dayjs>
                id={rest.id ?? id}
                {...rest}
                value={value}
                defaultValue={defaultValue}
                disabled={disabled}
                format={format ?? 'YYYY-MM-DD'}
                bordered={false}
                className={styles.control}
                popupClassName={cn(styles.popup, popupClassName)}
                prevIcon={<ChevronLeftIcon className={styles.navIcon} />}
                nextIcon={<ChevronRightIcon className={styles.navIcon} />}
                superPrevIcon={null}
                superNextIcon={null}
                suffixIcon={
                    suffixIcon ?? (
                        <span className={styles.iconWrapper}>
                            <CalendarIcon className={styles.icon} />
                        </span>
                    )
                }
                onFocus={event => {
                    setFocused(true);
                    onFocus?.(event, { range: undefined });
                }}
                onBlur={event => {
                    setFocused(false);
                    onBlur?.(event, { range: undefined });
                }}
                onOpenChange={(nextOpen: boolean) => {
                    setOpen(nextOpen);
                    onOpenChange?.(nextOpen);
                }}
                placeholder={shouldFloatLabel ? placeholder : ''}
            />

            {label && (
                <label
                    htmlFor={rest.id ?? id}
                    className={cn(
                        styles.label,
                        shouldFloatLabel ? styles.asLabel : styles.asPlaceholder
                    )}
                >
                    {label}
                    {requiredMark && (
                        <span className={styles.requiredMark}>*</span>
                    )}
                    {footnoteMark && (
                        <span className={styles.footnoteMark}>*</span>
                    )}
                </label>
            )}
        </div>
    );
};
